import {lazy, Suspense, useEffect} from 'react';
import {Spin} from 'antd';
import Container from 'typedi';
import {useTranslation} from 'react-i18next';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {RouteType} from '@/types';
import uniqueId from 'lodash/uniqueId';
import {PageRoute} from '@/constants';
import {AuthService} from '@/services';
import NotFoundPage from '@/pages/Error';
import {Loader} from '@/components/Loader';
import AuthLayout from '@/layouts/AuthLayout';
import MainLayout from '@/layouts/MainLayout';
import RequireAuth from '@/routers/RequireAuth';
import {TitlePage} from '@/components/TitlePage';
import ValidateLogin from '@/routers/ValidateLogin';
import {
  createPrivateRoutes,
  createPublicRoutes,
} from '@/utils/routers/use.router';
import '@/locales/i18n';
import { PartnerService } from '@/services/core/partner.service';
import { PermissionsPartnerDTO } from '@/shared/dto/partner.dto';

const renderRoutes = (routes: RouteType[]) =>
  routes.map(({element: Element, ...pageOptions}) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const routeOptions: any = pageOptions.index
      ? {index: true}
      : {path: pageOptions.path};

    return (
      <Route
        key={uniqueId('__page__')}
        path={pageOptions.path}
        element={
          <Suspense fallback={<Loader />}>
            <TitlePage title={pageOptions.title}>
              <Element />
            </TitlePage>
          </Suspense>
        }
        {...routeOptions}
      >
        {pageOptions?.children?.map(
          ({element: ChildrenElement, ...childrenOption}) =>
            childrenOption.index ? (
              <Route
                key={uniqueId('__page__')}
                index
                element={
                  <Suspense fallback={<Loader />}>
                    <TitlePage title={childrenOption.title}>
                      <ChildrenElement />
                    </TitlePage>
                  </Suspense>
                }
              />
            ) : (
              <Route
                key={uniqueId('__page__')}
                path={childrenOption.path}
                element={
                  <Suspense fallback={<Spin />}>
                    <TitlePage title={childrenOption.title}>
                      <ChildrenElement />
                    </TitlePage>
                  </Suspense>
                }
              >
                {childrenOption?.children &&
                  renderRoutes(childrenOption.children)}
              </Route>
            ),
        )}
      </Route>
    );
  });

export default function RoutesApp() {
  const lazyLoadRoute = (pageName: string) =>
    lazy(() => import(`../pages/${pageName}`));
  const {t} = useTranslation();
  const authService: AuthService = Container.get(AuthService);
  const partnerService = Container.get(PartnerService);

  const isSignedIn = authService.isSignedIn();

  if (isSignedIn === null) {
    return <Spin />;
  }

  useEffect(()=>{
    if (isSignedIn === true) {
      partnerService.getPermissionsPartner(new PermissionsPartnerDTO());
    }
  }, [isSignedIn])

  return (
    <Suspense fallback={<Spin />}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route
            element={
              <ValidateLogin>
                <AuthLayout />
              </ValidateLogin>
            }
          >
            {renderRoutes(createPublicRoutes(t, lazyLoadRoute))}
          </Route>
          <Route
            element={
              <RequireAuth>
                <MainLayout />
              </RequireAuth>
            }
          >
            {renderRoutes(createPrivateRoutes(t, lazyLoadRoute))}
          </Route>
          <Route path={PageRoute.NOT_FOUND} element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
}
